import type {
  OfflineAuthProfile,
  OfflineLoginCredentials,
  OfflinePosPersistedState,
  OfflineUserSnapshot,
} from './types.js';
import type { OfflineAuthCrypto } from './auth-crypto.js';

export const MAX_AUTH_PROFILES = 5;

export interface CacheAuthProfileInput {
  username: string;
  branchId: string;
  branchName?: string;
  user: OfflineUserSnapshot;
  password: string;
  rememberMe: boolean;
  refreshToken?: string;
}

function isAdminRole(role: string): boolean {
  return role.toLowerCase() === 'admin';
}

function profileKey(username: string, branchId: string): string {
  return `${username.toLowerCase()}::${branchId}`;
}

export async function upsertAuthProfile(
  state: OfflinePosPersistedState,
  input: CacheAuthProfileInput,
  crypto: OfflineAuthCrypto,
): Promise<OfflinePosPersistedState> {
  if (!input.rememberMe) {
    return state;
  }

  const credentialVerifier = await crypto.createCredentialVerifier(input.password);
  const encryptedRefreshToken = input.refreshToken
    ? await crypto.sealRefreshToken(input.refreshToken)
    : undefined;

  const nextProfile: OfflineAuthProfile = {
    username: input.username,
    branchId: input.branchId,
    branchName: input.branchName,
    user: input.user,
    credentialVerifier,
    encryptedRefreshToken,
    lastOnlineLoginAt: new Date().toISOString(),
  };

  const key = profileKey(input.username, input.branchId);
  const without = state.authProfiles.filter(
    (p) => profileKey(p.username, p.branchId) !== key,
  );
  const merged = [nextProfile, ...without].slice(0, MAX_AUTH_PROFILES);

  return { ...state, authProfiles: merged };
}

export function findAuthProfile(
  state: OfflinePosPersistedState,
  username: string,
  branchId: string,
): OfflineAuthProfile | null {
  const normalized = username.toLowerCase();
  const exact = state.authProfiles.find(
    (p) => p.username.toLowerCase() === normalized && p.branchId === branchId,
  );
  if (exact) return exact;

  const byUsername = state.authProfiles.filter(
    (p) => p.username.toLowerCase() === normalized,
  );
  if (byUsername.length === 0) return null;

  const adminProfile = byUsername.find((p) => isAdminRole(p.user.role));
  return adminProfile ?? byUsername[0] ?? null;
}

export async function verifyOfflineLogin(
  state: OfflinePosPersistedState,
  credentials: OfflineLoginCredentials,
  crypto: OfflineAuthCrypto,
): Promise<{ ok: true; profile: OfflineAuthProfile } | { ok: false; reason: string }> {
  const profile = findAuthProfile(state, credentials.username, credentials.branchId);
  if (!profile) {
    return { ok: false, reason: 'No remembered account for this branch.' };
  }

  if (!isAdminRole(profile.user.role) && profile.branchId !== credentials.branchId) {
    return { ok: false, reason: 'Branch not authorized for this account.' };
  }

  if (!(await crypto.verifyCredential(credentials.password, profile.credentialVerifier))) {
    return { ok: false, reason: 'Invalid credentials.' };
  }

  return { ok: true, profile };
}

export function removeAuthProfile(
  state: OfflinePosPersistedState,
  username: string,
  branchId: string,
): OfflinePosPersistedState {
  const key = profileKey(username, branchId);
  return {
    ...state,
    authProfiles: state.authProfiles.filter(
      (p) => profileKey(p.username, p.branchId) !== key,
    ),
  };
}
